import React, { useEffect, useMemo, useState, useRef, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Save, Eraser, Trash2, Trees, Play, Sparkles, MousePointer2, PaintBucket, Library, Users, BoxSelect, Globe, Copy, Search } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/Bits";
import { toast } from "sonner";

const GRID = 24;

const GROUPS = [
  { key: "terrain", name: "Terrain", items: [
    { type: "grass", emoji: "🟩" }, { type: "sand", emoji: "🟨" }, { type: "snow", emoji: "⬜" }, { type: "stone", emoji: "⬛" }, { type: "water_ground", emoji: "🟦" },
  ] },
  { key: "nature", name: "Nature", items: [
    { type: "tree", emoji: "🌳" }, { type: "pine", emoji: "🌲" }, { type: "bush", emoji: "🌿" }, { type: "flower", emoji: "🌸" }, { type: "mushroom", emoji: "🍄" }, { type: "rock", emoji: "🪨" }, { type: "crystal", emoji: "💎" }, { type: "water", emoji: "💧" },
  ] },
  { key: "build", name: "Structures", items: [
    { type: "lamp", emoji: "🏮" }, { type: "campfire", emoji: "🔥" }, { type: "chest", emoji: "🎁" }, { type: "barrel", emoji: "🛢️" }, { type: "pillar", emoji: "🏛️" }, { type: "signpost", emoji: "🪧" },
  ] },
  { key: "play", name: "Gameplay (plays for real)", items: [
    { type: "enemy", emoji: "👾" }, { type: "coin", emoji: "🪙" }, { type: "lava", emoji: "🌋" }, { type: "spikes", emoji: "🔺" }, { type: "saw", emoji: "⚙️" },
  ] },
  { key: "char", name: "Characters", items: [
    { type: "hero", emoji: "🦸" }, { type: "ally", emoji: "🛡️" }, { type: "squad", emoji: "⚔️" }, { type: "boss", emoji: "👹" },
  ] },
];
const EMOJI = GROUPS.reduce((m, g) => { g.items.forEach((it) => { m[it.type] = it.emoji; }); return m; }, {});
const COUNT_TYPES = new Set(["enemy", "coin", "ally", "lava", "spikes", "saw"]);
const CHAR_TOOLS = new Set(["hero", "ally", "squad", "boss"]);
const COLOR_SWATCHES = ["#4ade80", "#60a5fa", "#f472b6", "#fbbf24", "#f87171", "#a78bfa", "#34d399", "#ffffff", "#94a3b8", "#0b1026"];
const FORMATIONS = [
  { key: "wedge", label: "Wedge" }, { key: "line", label: "Line" }, { key: "circle", label: "Circle" }, { key: "diamond", label: "Diamond" },
];
const COMMUNITY_TAGS = ["combat", "boss", "loot", "nature", "hazard", "structures", "allies"];

const WORLD_KEYWORDS = { tiny: 40, small: 60, medium: 90, large: 150, huge: 230, vast: 360, endless: 500 };
function worldSizeOf(config) {
  const raw = config?.world?.worldSize;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return Math.max(20, Math.min(n, 600));
  return WORLD_KEYWORDS[String(raw || "").toLowerCase()] || 90;
}

export default function MapStudio() {
  const { gameId } = useParams();
  const navigate = useNavigate();
  const [game, setGame] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [populating, setPopulating] = useState(false);
  const [tool, setTool] = useState("tree");
  const [brush, setBrush] = useState(1);
  const [fillMode, setFillMode] = useState(false);
  const [props, setProps] = useState([]);
  const [chars, setChars] = useState([]);
  const [selectedChar, setSelectedChar] = useState(null);
  const [inspectId, setInspectId] = useState(null);
  // squad config
  const [squadSize, setSquadSize] = useState(4);
  const [squadFormation, setSquadFormation] = useState("wedge");
  const [squadName, setSquadName] = useState("");
  const [squadMembers, setSquadMembers] = useState([]);
  const [rosters, setRosters] = useState([]);
  const [rosterSaving, setRosterSaving] = useState(false);
  // multi-select
  const [selectedIds, setSelectedIds] = useState([]);
  const [marquee, setMarquee] = useState(null); // {a:{gx,gz}, b:{gx,gz}}
  // templates
  const [templates, setTemplates] = useState([]);
  const [community, setCommunity] = useState([]);
  const [tplOpen, setTplOpen] = useState(false);
  const [tplTab, setTplTab] = useState("mine");
  const [communitySort, setCommunitySort] = useState("trending");
  const [communityQuery, setCommunityQuery] = useState("");
  const [communityTag, setCommunityTag] = useState("");
  const [tplName, setTplName] = useState("");
  const [tplSaving, setTplSaving] = useState(false);
  const paintingRef = useRef(false);
  const selectingRef = useRef(false);
  const selectAnchorRef = useRef(null);
  const gridRef = useRef(null);
  const propsRef = useRef(props);
  propsRef.current = props;

  const loadTemplates = useCallback(() => {
    api.get("/map-templates").then((r) => setTemplates(r.data?.templates || [])).catch(() => {});
  }, []);
  const loadRosters = useCallback(() => {
    api.get("/squad-rosters").then((r) => setRosters(r.data?.rosters || [])).catch(() => {});
  }, []);
  const loadCommunity = useCallback((sort, q, tag) => {
    api.get("/map-templates/community", { params: { sort: sort || "trending", q: q || "", tag: tag || "" } })
      .then((r) => setCommunity(r.data?.templates || [])).catch(() => {});
  }, []);

  useEffect(() => {
    let ignore = false;
    api.get(`/games/${gameId}`)
      .then((r) => { if (ignore) return; setGame(r.data); setProps(Array.isArray(r.data?.config?.props) ? r.data.config.props : []); })
      .catch(() => { if (!ignore) toast.error("Couldn't load this game."); })
      .finally(() => { if (!ignore) setLoading(false); });
    api.get("/characters").then((r) => {
      if (ignore) return;
      const arr = Array.isArray(r.data) ? r.data : (r.data?.items || []);
      setChars(arr.slice(0, 24).map((c) => ({
        id: c.id, name: c.name || "Hero",
        emoji: c.emoji || c.avatar_emoji || "🧙",
        color: c.color || c.accent || "",
      })));
    }).catch(() => {});
    loadTemplates();
    loadRosters();
    return () => { ignore = true; };
  }, [gameId, loadTemplates, loadRosters]);

  useEffect(() => {
    const up = () => { paintingRef.current = false; selectingRef.current = false; };
    window.addEventListener("pointerup", up);
    return () => window.removeEventListener("pointerup", up);
  }, []);

  const ws = useMemo(() => worldSizeOf(game?.config), [game]);
  const half = ws / 2;
  const cell = ws / GRID;

  const cellToWorld = useCallback((gx, gz) => ({
    x: Math.round(((gx + 0.5) * cell - half) * 100) / 100,
    z: Math.round(((gz + 0.5) * cell - half) * 100) / 100,
  }), [cell, half]);
  const worldToCell = useCallback((x, z) => ({
    gx: Math.max(0, Math.min(GRID - 1, Math.floor((x + half) / cell))),
    gz: Math.max(0, Math.min(GRID - 1, Math.floor((z + half) / cell))),
  }), [cell, half]);

  const occupied = useMemo(() => {
    const m = {};
    for (const p of props) { const { gx, gz } = worldToCell(p.x, p.z); m[`${gx}-${gz}`] = p; }
    return m;
  }, [props, worldToCell]);

  const isCharTool = CHAR_TOOLS.has(tool);
  const inspected = useMemo(() => props.find((p) => p.id === inspectId) || null, [props, inspectId]);
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  const applyToolAt = useCallback((list, gx, gz) => {
    const existing = list.find((p) => { const c = worldToCell(p.x, p.z); return c.gx === gx && c.gz === gz; });
    if (tool === "eraser") return existing ? list.filter((p) => p !== existing) : list;
    if (existing && existing.type === tool && (!isCharTool || existing.char?.id === selectedChar?.id)) return list;
    const rest = existing ? list.filter((p) => p !== existing) : list;
    const { x, z } = cellToWorld(gx, gz);
    const id = existing ? existing.id : `${tool}_${gx}_${gz}_${Date.now().toString(36)}${Math.floor(Math.random() * 999).toString(36)}`;
    const np = { id, type: tool, x, z };
    if (isCharTool && selectedChar) np.char = { id: selectedChar.id, name: selectedChar.name, emoji: selectedChar.emoji, color: selectedChar.color };
    if (tool === "squad") {
      np.formation = squadFormation;
      if (squadName.trim()) np.name = squadName.trim();
      if (squadMembers.length) {
        np.members = squadMembers.map((m) => ({ id: m.id, name: m.name, emoji: m.emoji, color: m.color }));
        np.count = squadMembers.length;
        np.char = { ...np.members[0] };
      } else { np.count = squadSize; }
    }
    return [...rest, np];
  }, [tool, isCharTool, selectedChar, squadSize, squadFormation, squadName, squadMembers, worldToCell, cellToWorld]);

  const brushCells = useCallback((gx, gz) => {
    if (isCharTool) return [[gx, gz]];
    const r = Math.floor(brush / 2); const cells = [];
    for (let dz = -r; dz <= r; dz++) for (let dx = -r; dx <= r; dx++) {
      const nx = gx + dx, nz = gz + dz;
      if (nx >= 0 && nx < GRID && nz >= 0 && nz < GRID) cells.push([nx, nz]);
    }
    return cells;
  }, [brush, isCharTool]);

  const floodFill = useCallback((gx, gz) => {
    setProps((list) => {
      const occ = {};
      for (const p of list) { const c = worldToCell(p.x, p.z); occ[`${c.gx}-${c.gz}`] = p; }
      const startType = occ[`${gx}-${gz}`] ? occ[`${gx}-${gz}`].type : null;
      if (tool !== "eraser" && startType === tool) return list;
      const visited = new Set(); const stack = [[gx, gz]]; const targets = [];
      while (stack.length) {
        const [cx, cz] = stack.pop(); const key = `${cx}-${cz}`;
        if (cx < 0 || cx >= GRID || cz < 0 || cz >= GRID || visited.has(key)) continue;
        visited.add(key);
        const ptype = occ[key] ? occ[key].type : null;
        if (ptype !== startType) continue;
        targets.push([cx, cz]);
        stack.push([cx + 1, cz], [cx - 1, cz], [cx, cz + 1], [cx, cz - 1]);
      }
      let out = list;
      for (const [cx, cz] of targets) out = applyToolAt(out, cx, cz);
      return out;
    });
  }, [tool, worldToCell, applyToolAt]);

  const paintBrush = useCallback((gx, gz) => {
    setProps((list) => {
      let out = list;
      for (const [cx, cz] of brushCells(gx, gz)) out = applyToolAt(out, cx, cz);
      return out;
    });
  }, [brushCells, applyToolAt]);

  const selectInMarquee = useCallback((a, b) => {
    const minX = Math.min(a.gx, b.gx), maxX = Math.max(a.gx, b.gx);
    const minZ = Math.min(a.gz, b.gz), maxZ = Math.max(a.gz, b.gz);
    const ids = propsRef.current.filter((p) => { const c = worldToCell(p.x, p.z); return c.gx >= minX && c.gx <= maxX && c.gz >= minZ && c.gz <= maxZ; }).map((p) => p.id);
    setSelectedIds(ids);
  }, [worldToCell]);

  const onCellDown = (gx, gz) => {
    if (tool === "select") { selectingRef.current = true; const a = { gx, gz }; selectAnchorRef.current = a; setMarquee({ a, b: a }); selectInMarquee(a, a); return; }
    if (tool === "inspect") { const p = occupied[`${gx}-${gz}`]; setInspectId(p ? p.id : null); return; }
    if (fillMode) { floodFill(gx, gz); return; }
    paintingRef.current = true; paintBrush(gx, gz);
  };
  const onCellEnter = (gx, gz) => {
    if (tool === "select" && selectingRef.current && selectAnchorRef.current) { const a = selectAnchorRef.current, b = { gx, gz }; setMarquee({ a, b }); selectInMarquee(a, b); return; }
    if (paintingRef.current && !fillMode && tool !== "inspect") paintBrush(gx, gz);
  };

  // Container-level pointer tracking (buttons implicitly capture the pointer, so onPointerEnter
  // on sibling cells won't fire mid-drag — we map pointer coords → cell here instead).
  const cellFromEvent = (e) => {
    const el = gridRef.current; if (!el) return null;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    const gx = Math.floor(((e.clientX - r.left) / r.width) * GRID);
    const gz = Math.floor(((e.clientY - r.top) / r.height) * GRID);
    if (gx < 0 || gx >= GRID || gz < 0 || gz >= GRID) return null;
    return { gx, gz };
  };
  const onGridPointerMove = (e) => {
    if (tool === "select" && selectingRef.current && selectAnchorRef.current) {
      const c = cellFromEvent(e); if (!c) return;
      const a = selectAnchorRef.current; setMarquee({ a, b: c }); selectInMarquee(a, c);
      return;
    }
    if (paintingRef.current && !fillMode && tool !== "inspect" && tool !== "select") {
      const c = cellFromEvent(e); if (c) paintBrush(c.gx, c.gz);
    }
  };

  const updateProp = (id, patch) => setProps((list) => list.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const deleteProp = (id) => { setProps((list) => list.filter((p) => p.id !== id)); setInspectId(null); };

  const bulkPatch = (patch) => setProps((list) => list.map((p) => (selectedSet.has(p.id) ? { ...p, ...patch } : p)));
  const bulkDelete = () => { setProps((list) => list.filter((p) => !selectedSet.has(p.id))); setSelectedIds([]); setMarquee(null); };
  const clearSelection = () => { setSelectedIds([]); setMarquee(null); };

  const pickTool = (t) => {
    setTool(t);
    if (t !== "inspect") setInspectId(null);
    if (t !== "select") { setSelectedIds([]); setMarquee(null); }
    if (t === "eraser" || t === "inspect" || t === "select") setFillMode(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/games/${gameId}/config`, {
        config: { props },
        summary: `Updated world map (${props.length} element${props.length === 1 ? "" : "s"})`,
      });
      toast.success("World map saved!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save the map."); }
    finally { setSaving(false); }
  };

  const populate = async () => {
    if (!props.length) { toast.error("Place a few things first, then let the copilot populate."); return; }
    setPopulating(true);
    try {
      const tally = props.reduce((m, p) => { m[p.type] = (m[p.type] || 0) + 1; return m; }, {});
      const list = Object.entries(tally).map(([t, n]) => `${n}× ${t}`).join(", ");
      const instruction = `I hand-built a world with these elements: ${list}. Add a few fitting quests, some loot rewards, and one or two light encounters that suit this layout. Keep it friendly and do NOT remove anything I positioned.`;
      const { data } = await api.post(`/games/${gameId}/edit`, { instruction }, { timeout: 60000 });
      await api.patch(`/games/${gameId}/config`, { config: { props }, summary: "Keep placements after copilot populate" });
      toast.success(data?.summary ? `Copilot: ${data.summary}` : "Copilot populated your world!");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Copilot couldn't populate right now."); }
    finally { setPopulating(false); }
  };

  const saveTemplate = async () => {
    const name = tplName.trim();
    if (!name) { toast.error("Give your template a name."); return; }
    if (!props.length) { toast.error("Place a few things before saving a template."); return; }
    setTplSaving(true);
    try {
      await api.post("/map-templates", { name, props, world_size: ws });
      toast.success(`Saved “${name}” to your templates!`);
      setTplName("");
      loadTemplates();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save the template."); }
    finally { setTplSaving(false); }
  };

  const applyTemplate = (t) => {
    setProps(Array.isArray(t.props) ? t.props.map((p) => ({ ...p })) : []);
    setInspectId(null); clearSelection();
    toast.success(`Loaded “${t.name}” — press Save map to keep it.`);
  };

  const deleteTemplate = async (t) => {
    try { await api.delete(`/map-templates/${t.id}`); setTemplates((l) => l.filter((x) => x.id !== t.id)); }
    catch { toast.error("Couldn't delete that template."); }
  };

  const togglePublish = async (t) => {
    const makePublic = t.visibility !== "public";
    try {
      await api.post(`/map-templates/${t.id}/publish`, { public: makePublic });
      setTemplates((l) => l.map((x) => (x.id === t.id ? { ...x, visibility: makePublic ? "public" : "private" } : x)));
      toast.success(makePublic ? "Published to the community!" : "Made private.");
      if (tplTab === "community") loadCommunity(communitySort, communityQuery, communityTag);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't update sharing."); }
  };

  const remixTemplate = async (t) => {
    try {
      await api.post(`/map-templates/${t.id}/remix`);
      toast.success(`Remixed “${t.name}” into your templates!`);
      loadTemplates(); setTplTab("mine");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't remix that template."); }
  };

  const openTab = (tab) => { setTplTab(tab); if (tab === "community") loadCommunity(communitySort, communityQuery, communityTag); };
  const changeCommunitySort = (s) => { setCommunitySort(s); loadCommunity(s, communityQuery, communityTag); };
  const runCommunitySearch = () => loadCommunity(communitySort, communityQuery, communityTag);
  const toggleTag = (t) => { const nt = communityTag === t ? "" : t; setCommunityTag(nt); loadCommunity(communitySort, communityQuery, nt); };

  // ---- Squad members (multi-select) + Rosters ----
  const toggleSquadMember = (c) => setSquadMembers((l) => (l.some((m) => m.id === c.id) ? l.filter((m) => m.id !== c.id) : [...l, { id: c.id, name: c.name, emoji: c.emoji, color: c.color }]));
  const saveRoster = async () => {
    if (!squadMembers.length) { toast.error("Pick at least one character for the roster."); return; }
    const name = squadName.trim() || "Squad";
    setRosterSaving(true);
    try {
      await api.post("/squad-rosters", { name, members: squadMembers, formation: squadFormation });
      toast.success(`Saved roster “${name}”!`);
      loadRosters();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Couldn't save the roster."); }
    finally { setRosterSaving(false); }
  };
  const loadRoster = (r) => {
    setTool("squad"); setSquadMembers((r.members || []).map((m) => ({ ...m }))); setSquadFormation(r.formation || "wedge"); setSquadName(r.name || "");
    toast.success(`Loaded roster “${r.name}” — click a cell to place, or hit Drop.`);
  };
  const placeRoster = (r) => {
    const half2 = Math.floor(GRID / 2);
    const { x, z } = cellToWorld(half2, half2);
    const id = `squad_${Date.now().toString(36)}${Math.floor(Math.random() * 999).toString(36)}`;
    const np = { id, type: "squad", x, z, members: (r.members || []).map((m) => ({ id: m.id, name: m.name, emoji: m.emoji, color: m.color })), count: (r.members || []).length, formation: r.formation || "wedge", name: r.name };
    if (np.members[0]) np.char = { ...np.members[0] };
    setProps((list) => [...list, np]);
    toast.success(`Dropped “${r.name}” squad — press Save map to keep it.`);
  };
  const deleteRoster = async (r) => {
    try { await api.delete(`/squad-rosters/${r.id}`); setRosters((l) => l.filter((x) => x.id !== r.id)); }
    catch { toast.error("Couldn't delete that roster."); }
  };

  if (loading) return <div className="min-h-[60vh] grid place-items-center"><Spinner /></div>;
  const is3d = game?.config?.dimension === "3d";

  return (
    <div className="max-w-6xl mx-auto px-4 py-6" data-testid="map-studio">
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Button variant="ghost" size="icon" onClick={() => navigate(`/app/build/${gameId}`)} data-testid="map-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <div className="min-w-0">
            <h1 className="font-display text-2xl font-bold flex items-center gap-2 truncate"><Trees className="w-6 h-6 text-emerald-500" /> Map Studio</h1>
            <p className="text-xs text-[hsl(var(--muted-foreground))] truncate">{game?.title || "Your world"} · build & populate your world</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="map-prop-count">{props.length} placed</span>
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => setTplOpen((v) => !v)} data-testid="map-templates-btn"><Library className="w-4 h-4" /> Templates</Button>
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => setProps([])} data-testid="map-clear-btn"><Trash2 className="w-4 h-4" /> Clear</Button>
          <Button variant="outline" size="sm" className="rounded-full" onClick={populate} disabled={populating} data-testid="map-populate-btn"><Sparkles className="w-4 h-4" /> {populating ? "Populating…" : "AI Populate"}</Button>
          <Button size="sm" className="rounded-full" onClick={save} disabled={saving} data-testid="map-save-btn"><Save className="w-4 h-4" /> {saving ? "Saving…" : "Save map"}</Button>
          <Button variant="outline" size="sm" className="rounded-full" onClick={() => navigate(`/app/play/${gameId}`)} data-testid="map-play-btn"><Play className="w-4 h-4" /> Play</Button>
        </div>
      </div>

      {/* Templates library */}
      {tplOpen && (
        <div className="mb-4 rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-4 space-y-3" data-testid="map-templates-panel">
          <div className="flex items-center gap-2">
            <button onClick={() => openTab("mine")} data-testid="map-tpl-tab-mine" className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${tplTab === "mine" ? "border-emerald-500 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>My templates</button>
            <button onClick={() => openTab("community")} data-testid="map-tpl-tab-community" className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors inline-flex items-center gap-1.5 ${tplTab === "community" ? "border-sky-500 bg-sky-500/15 text-sky-600 dark:text-sky-400" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}><Globe className="w-3.5 h-3.5" /> Community</button>
          </div>

          {tplTab === "mine" ? (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <input
                  value={tplName}
                  onChange={(e) => setTplName(e.target.value)}
                  placeholder="Name this map template…"
                  maxLength={60}
                  data-testid="map-template-name"
                  className="flex-1 min-w-[160px] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm"
                />
                <Button size="sm" className="rounded-full" onClick={saveTemplate} disabled={tplSaving} data-testid="map-save-template-btn"><Save className="w-4 h-4" /> {tplSaving ? "Saving…" : "Save as template"}</Button>
              </div>
              {templates.length === 0 ? (
                <p className="text-xs text-[hsl(var(--muted-foreground))]">No saved templates yet. Build a map you love, then save it here to reuse — or publish it to the community.</p>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {templates.map((t) => (
                    <div key={t.id} className="rounded-xl border border-[hsl(var(--border))] p-3 space-y-2" data-testid={`map-template-${t.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{t.name}</div>
                        <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{t.count || (t.props || []).length} elements{t.visibility === "public" ? " · public" : ""}</div>
                      </div>
                      <div className="flex items-center gap-1 flex-wrap">
                        <Button variant="outline" size="sm" className="rounded-full h-8 px-3" onClick={() => applyTemplate(t)} data-testid={`map-template-apply-${t.id}`}>Load</Button>
                        <Button variant="outline" size="sm" className={`rounded-full h-8 px-3 ${t.visibility === "public" ? "text-sky-600 dark:text-sky-400 border-sky-500/50" : ""}`} onClick={() => togglePublish(t)} data-testid={`map-template-publish-${t.id}`}><Globe className="w-3.5 h-3.5" /> {t.visibility === "public" ? "Published" : "Publish"}</Button>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500 ml-auto" onClick={() => deleteTemplate(t)} data-testid={`map-template-delete-${t.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Sort</span>
                <button onClick={() => changeCommunitySort("trending")} data-testid="map-community-sort-trending" className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${communitySort === "trending" ? "border-amber-500 bg-amber-500/15 text-amber-600 dark:text-amber-400" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>🔥 Trending</button>
                <button onClick={() => changeCommunitySort("recent")} data-testid="map-community-sort-recent" className={`px-3 py-1 rounded-full text-xs font-semibold border transition-colors ${communitySort === "recent" ? "border-emerald-500 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>Recent</button>
              </div>
              <div className="flex items-center gap-2">
                <input
                  value={communityQuery}
                  onChange={(e) => setCommunityQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") runCommunitySearch(); }}
                  placeholder="Search maps by name…"
                  maxLength={60}
                  data-testid="map-community-search"
                  className="flex-1 min-w-[150px] rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm"
                />
                <Button variant="outline" size="sm" className="rounded-full" onClick={runCommunitySearch} data-testid="map-community-search-btn"><Search className="w-4 h-4" /> Search</Button>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {COMMUNITY_TAGS.map((tg) => (
                  <button key={tg} onClick={() => toggleTag(tg)} data-testid={`map-community-tag-${tg}`} className={`px-2.5 py-1 rounded-full text-[11px] font-semibold border capitalize transition-colors ${communityTag === tg ? "border-violet-500 bg-violet-500/15 text-violet-600 dark:text-violet-400" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>{tg}</button>
                ))}
              </div>
              {community.length === 0 ? (
                <p className="text-xs text-[hsl(var(--muted-foreground))]" data-testid="map-community-empty">No maps match. Try clearing the search or a different tag.</p>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {community.map((t) => (
                    <div key={t.id} className="rounded-xl border border-[hsl(var(--border))] p-3 space-y-2" data-testid={`map-community-${t.id}`}>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold truncate">{t.name}</div>
                        <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{t.count || (t.props || []).length} elements · by {t.owner_name || "Creator"} · <span data-testid={`map-community-remixes-${t.id}`}>🔁 {t.remix_count || 0}</span></div>
                        {Array.isArray(t.tags) && t.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1 mt-1">
                            {t.tags.slice(0, 4).map((tg) => (<span key={tg} className="px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))] capitalize">{tg}</span>))}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        <Button variant="outline" size="sm" className="rounded-full h-8 px-3" onClick={() => applyTemplate(t)} data-testid={`map-community-apply-${t.id}`}>Load</Button>
                        <Button size="sm" className="rounded-full h-8 px-3" onClick={() => remixTemplate(t)} data-testid={`map-community-remix-${t.id}`}><Copy className="w-3.5 h-3.5" /> Remix</Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Brush & Fill controls */}
      <div className="flex items-center gap-3 mb-4 flex-wrap rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] px-4 py-2.5">
        <span className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))]">Brush</span>
        <div className="flex items-center gap-1">
          {[1, 3, 5].map((b) => (
            <button
              key={b}
              onClick={() => setBrush(b)}
              disabled={isCharTool || tool === "select" || tool === "inspect"}
              data-testid={`map-brush-${b}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-40 ${brush === b ? "border-emerald-500 bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}
            >
              {b}×{b}
            </button>
          ))}
        </div>
        <div className="w-px h-6 bg-[hsl(var(--border))]" />
        <button
          onClick={() => { setFillMode((v) => !v); if (tool === "inspect" || tool === "select") pickTool("tree"); }}
          data-testid="map-fill-toggle"
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors inline-flex items-center gap-1.5 ${fillMode ? "border-sky-500 bg-sky-500/15 text-sky-600 dark:text-sky-400" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}
        >
          <PaintBucket className="w-4 h-4" /> Fill {fillMode ? "on" : "off"}
        </button>
        <button
          onClick={() => pickTool("inspect")}
          data-testid="map-tool-inspect"
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors inline-flex items-center gap-1.5 ${tool === "inspect" ? "border-violet-500 bg-violet-500/15 text-violet-600 dark:text-violet-400" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}
        >
          <MousePointer2 className="w-4 h-4" /> Inspect
        </button>
        <button
          onClick={() => pickTool("select")}
          data-testid="map-tool-select"
          className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition-colors inline-flex items-center gap-1.5 ${tool === "select" ? "border-amber-500 bg-amber-500/15 text-amber-600 dark:text-amber-400" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}
        >
          <BoxSelect className="w-4 h-4" /> Select
        </button>
        <span className="text-[11px] text-[hsl(var(--muted-foreground))] ml-auto hidden sm:block">
          {tool === "select" ? "Drag a box to select items, then bulk edit." : tool === "inspect" ? "Click a placed item to tweak it." : fillMode ? "Click to flood-fill matching cells." : `Paint a ${brush}×${brush} area.`}
        </span>
      </div>

      {!is3d && (
        <div className="mb-4 rounded-xl border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-xs text-amber-700 dark:text-amber-300" data-testid="map-2d-note">
          Placed elements come alive in 3D worlds. You can design here now — switch this game to a 3D engine to play them.
        </div>
      )}

      <div className="grid md:grid-cols-[210px_1fr] gap-5">
        {/* Palette */}
        <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 h-fit space-y-3">
          {GROUPS.map((grp) => (
            <div key={grp.key}>
              <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1.5">{grp.name}</p>
              <div className="grid grid-cols-5 gap-1.5">
                {grp.items.map((it) => (
                  <button
                    key={it.type}
                    onClick={() => pickTool(it.type)}
                    data-testid={`map-tool-${it.type}`}
                    title={it.type}
                    className={`aspect-square rounded-lg text-lg grid place-items-center border transition-colors ${tool === it.type ? "border-emerald-500 bg-emerald-500/15" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}
                  >
                    {it.emoji}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <button
            onClick={() => pickTool("eraser")}
            data-testid="map-tool-eraser"
            className={`w-full rounded-lg py-1.5 grid place-items-center border transition-colors ${tool === "eraser" ? "border-red-500 bg-red-500/15 text-red-500" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}
          >
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold"><Eraser className="w-4 h-4" /> Eraser</span>
          </button>
          <p className="text-[11px] text-[hsl(var(--muted-foreground))] leading-relaxed">Click or drag on the grid to place. Grid maps to your world ({Math.round(ws)}×{Math.round(ws)}). ⚔️ Squads move in formation & revive each other!</p>
        </div>

        {/* Grid + character picker + inspector + squad + multi-select */}
        <div className="space-y-3">
          {isCharTool && (
            <div className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3" data-testid="map-char-picker">
              <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-2 flex items-center gap-1.5">
                {(tool === "ally" || tool === "squad") && <Users className="w-3.5 h-3.5" />}
                {tool === "boss" ? "Pick a character to place as a boss" : tool === "squad" ? "Pick characters for your squad roster (multi-select)" : tool === "ally" ? "Pick a character to place as a battle ally" : "Pick a character to place as the hero start"}
              </p>
              {chars.length === 0 ? (
                <p className="text-xs text-[hsl(var(--muted-foreground))]">No saved characters yet — placing a default {tool}. Make some in Character Studio to drop them here.</p>
              ) : (
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {chars.map((c) => {
                    const isMember = tool === "squad" && squadMembers.some((m) => m.id === c.id);
                    const active = tool === "squad" ? isMember : selectedChar?.id === c.id;
                    return (
                      <button
                        key={c.id}
                        onClick={() => (tool === "squad" ? toggleSquadMember(c) : setSelectedChar(selectedChar?.id === c.id ? null : c))}
                        data-testid={`map-char-${c.id}`}
                        className={`relative shrink-0 w-16 rounded-xl border p-2 text-center transition-colors ${active ? "border-emerald-500 bg-emerald-500/15" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}
                      >
                        {isMember && <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-emerald-500 text-white text-[9px] font-bold grid place-items-center">✓</span>}
                        <div className="text-2xl leading-none">{c.emoji || "🧙"}</div>
                        <div className="text-[10px] truncate mt-1">{c.name || "Hero"}</div>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {/* Squad config + rosters */}
          {tool === "squad" && (
            <div className="rounded-2xl border border-sky-500/40 bg-sky-500/5 p-4 space-y-3" data-testid="map-squad-panel">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold">Squad size</span>
                {[2, 3, 4, 5, 6, 8].map((n) => (
                  <button key={n} onClick={() => setSquadSize(n)} disabled={squadMembers.length > 0} data-testid={`map-squad-size-${n}`} className={`w-8 h-8 rounded-lg text-xs font-semibold border transition-colors disabled:opacity-40 ${squadSize === n ? "border-sky-500 bg-sky-500/15 text-sky-600 dark:text-sky-400" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>{n}</button>
                ))}
                {squadMembers.length > 0 && <span className="text-[11px] text-sky-600 dark:text-sky-400 font-semibold">= {squadMembers.length} members</span>}
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold">Formation</span>
                {FORMATIONS.map((f) => (
                  <button key={f.key} onClick={() => setSquadFormation(f.key)} data-testid={`map-squad-formation-${f.key}`} className={`px-3 h-8 rounded-lg text-xs font-semibold border transition-colors ${squadFormation === f.key ? "border-sky-500 bg-sky-500/15 text-sky-600 dark:text-sky-400" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}>{f.label}</button>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <input
                  value={squadName}
                  onChange={(e) => setSquadName(e.target.value)}
                  placeholder="Roster / squad name (e.g. Ember Guard)"
                  maxLength={40}
                  data-testid="map-squad-name"
                  className="flex-1 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--background))] px-3 py-2 text-sm"
                />
                <Button size="sm" className="rounded-full" onClick={saveRoster} disabled={rosterSaving || squadMembers.length === 0} data-testid="map-save-roster-btn"><Save className="w-4 h-4" /> {rosterSaving ? "Saving…" : "Save roster"}</Button>
              </div>
              <p className="text-[11px] text-[hsl(var(--muted-foreground))]">{squadMembers.length ? `Drops a ${squadMembers.length}-member roster` : `Drops ${squadSize} allies`} in a {squadFormation} that move together, share HP bars, and revive fallen squadmates.</p>
              {rosters.length > 0 && (
                <div className="pt-1 border-t border-[hsl(var(--border))]" data-testid="map-roster-list">
                  <p className="text-[11px] font-bold uppercase tracking-wide text-[hsl(var(--muted-foreground))] mb-1.5 mt-2">My rosters</p>
                  <div className="grid sm:grid-cols-2 gap-2">
                    {rosters.map((r) => (
                      <div key={r.id} className="rounded-xl border border-[hsl(var(--border))] p-2.5 space-y-1.5" data-testid={`map-roster-${r.id}`}>
                        <div className="flex items-center justify-between gap-2">
                          <div className="min-w-0">
                            <div className="text-sm font-semibold truncate">{r.name}</div>
                            <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{(r.members || []).map((m) => m.emoji || "🧙").join(" ")} · {r.formation}</div>
                          </div>
                          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 shrink-0" onClick={() => deleteRoster(r)} data-testid={`map-roster-delete-${r.id}`}><Trash2 className="w-4 h-4" /></Button>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button size="sm" className="rounded-full h-7 px-3 flex-1" onClick={() => placeRoster(r)} data-testid={`map-roster-place-${r.id}`}><Users className="w-3.5 h-3.5" /> Drop</Button>
                          <Button variant="outline" size="sm" className="rounded-full h-7 px-3" onClick={() => loadRoster(r)} data-testid={`map-roster-load-${r.id}`}>Edit</Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Multi-select bulk panel */}
          {tool === "select" && (
            <div className="rounded-2xl border border-amber-500/40 bg-amber-500/5 p-4 min-h-[210px]" data-testid="map-multi-panel">
              {selectedIds.length === 0 ? (
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Drag a box across the grid to select several placed items, then retint, resize, or delete them all at once.</p>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold" data-testid="map-multi-count">{selectedIds.length} selected</span>
                    <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={clearSelection} data-testid="map-multi-clear">Clear</Button>
                  </div>
                  <div>
                    <div className="text-xs font-semibold mb-1">Resize all</div>
                    <input type="range" min="0.4" max="3" step="0.1" defaultValue="1" onChange={(e) => bulkPatch({ scale: Number(e.target.value) })} data-testid="map-multi-scale" className="w-full accent-amber-500" />
                  </div>
                  <div>
                    <div className="text-xs font-semibold mb-1.5">Retint all</div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {COLOR_SWATCHES.map((hex) => (
                        <button key={hex} onClick={() => bulkPatch({ color: hex })} data-testid={`map-multi-color-${hex.replace("#", "")}`} title={hex} className="w-6 h-6 rounded-full border-2 border-black/10 dark:border-white/20 transition-transform hover:scale-110" style={{ background: hex }} />
                      ))}
                      <input type="color" defaultValue="#4ade80" onChange={(e) => bulkPatch({ color: e.target.value })} data-testid="map-multi-color" className="w-7 h-7 rounded-md border border-[hsl(var(--border))] bg-transparent cursor-pointer p-0" />
                    </div>
                  </div>
                  <Button variant="outline" size="sm" className="rounded-full text-red-500 border-red-500/40" onClick={bulkDelete} data-testid="map-multi-delete"><Trash2 className="w-4 h-4" /> Delete selected</Button>
                </div>
              )}
            </div>
          )}

          {/* Prop Inspector */}
          {tool === "inspect" && (
            <div className="rounded-2xl border border-violet-500/40 bg-violet-500/5 p-4" data-testid="map-inspector">
              {!inspected ? (
                <p className="text-xs text-[hsl(var(--muted-foreground))]">Click any placed item on the grid to tweak its size, colour and count.</p>
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-2xl leading-none">{inspected.char?.emoji || EMOJI[inspected.type] || "▪"}</span>
                      <div className="min-w-0">
                        <div className="text-sm font-semibold capitalize truncate">{inspected.char?.name || inspected.type}</div>
                        <div className="text-[11px] text-[hsl(var(--muted-foreground))]">{inspected.type}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-red-500" onClick={() => deleteProp(inspected.id)} data-testid="map-inspector-delete"><Trash2 className="w-4 h-4" /></Button>
                      <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => setInspectId(null)} data-testid="map-inspector-close">Close</Button>
                    </div>
                  </div>

                  <div>
                    <div className="flex items-center justify-between text-xs mb-1"><span className="font-semibold">Size</span><span className="text-[hsl(var(--muted-foreground))]">{(inspected.scale || 1).toFixed(1)}×</span></div>
                    <input
                      type="range" min="0.4" max="3" step="0.1"
                      value={inspected.scale || 1}
                      onChange={(e) => updateProp(inspected.id, { scale: Number(e.target.value) })}
                      data-testid="map-inspector-scale"
                      className="w-full accent-violet-500"
                    />
                  </div>

                  <div>
                    <div className="text-xs font-semibold mb-1.5">Colour</div>
                    <div className="flex items-center gap-1.5 flex-wrap">
                      {COLOR_SWATCHES.map((hex) => (
                        <button
                          key={hex}
                          onClick={() => updateProp(inspected.id, { color: hex })}
                          data-testid={`map-inspector-color-${hex.replace("#", "")}`}
                          title={hex}
                          className={`w-6 h-6 rounded-full border-2 transition-transform hover:scale-110 ${(inspected.color || "").toLowerCase() === hex.toLowerCase() ? "border-violet-500" : "border-black/10 dark:border-white/20"}`}
                          style={{ background: hex }}
                        />
                      ))}
                      <input
                        type="color"
                        value={inspected.color || "#4ade80"}
                        onChange={(e) => updateProp(inspected.id, { color: e.target.value })}
                        data-testid="map-inspector-color"
                        className="w-7 h-7 rounded-md border border-[hsl(var(--border))] bg-transparent cursor-pointer p-0"
                      />
                    </div>
                  </div>

                  {COUNT_TYPES.has(inspected.type) && (
                    <div>
                      <div className="flex items-center justify-between text-xs mb-1"><span className="font-semibold">Count (cluster)</span><span className="text-[hsl(var(--muted-foreground))]">×{inspected.count || 1}</span></div>
                      <div className="flex items-center gap-1 flex-wrap">
                        {[1, 2, 3, 4, 5, 6, 8].map((n) => (
                          <button
                            key={n}
                            onClick={() => updateProp(inspected.id, { count: n })}
                            data-testid={`map-inspector-count-${n}`}
                            className={`w-8 h-8 rounded-lg text-xs font-semibold border transition-colors ${(inspected.count || 1) === n ? "border-violet-500 bg-violet-500/15 text-violet-600 dark:text-violet-400" : "border-[hsl(var(--border))] hover:bg-[hsl(var(--secondary))]"}`}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="rounded-2xl border border-[hsl(var(--border))] bg-[radial-gradient(circle_at_center,hsl(var(--secondary))_0%,hsl(var(--card))_100%)] p-2 overflow-auto">
            <div
              ref={gridRef}
              className="grid mx-auto aspect-square w-full max-w-[600px] rounded-lg overflow-hidden select-none touch-none"
              style={{ gridTemplateColumns: `repeat(${GRID}, minmax(0, 1fr))` }}
              onPointerMove={onGridPointerMove}
              onPointerLeave={() => { paintingRef.current = false; }}
              data-testid="map-grid"
            >
              {Array.from({ length: GRID * GRID }).map((_, i) => {
                const gx = i % GRID, gz = Math.floor(i / GRID);
                const p = occupied[`${gx}-${gz}`];
                const isSel = p && p.id === inspectId;
                const inMulti = p && selectedSet.has(p.id);
                const inBox = tool === "select" && marquee && gx >= Math.min(marquee.a.gx, marquee.b.gx) && gx <= Math.max(marquee.a.gx, marquee.b.gx) && gz >= Math.min(marquee.a.gz, marquee.b.gz) && gz <= Math.max(marquee.a.gz, marquee.b.gz);
                return (
                  <button
                    key={i}
                    onPointerDown={(e) => { e.preventDefault(); try { e.currentTarget.releasePointerCapture(e.pointerId); } catch (_) {} onCellDown(gx, gz); }}
                    onPointerEnter={() => onCellEnter(gx, gz)}
                    data-testid={`map-cell-${gx}-${gz}`}
                    className={`aspect-square grid place-items-center text-[13px] leading-none border transition-colors ${isSel ? "border-violet-500 bg-violet-500/25 ring-1 ring-violet-500" : inMulti ? "border-amber-500 bg-amber-500/30 ring-1 ring-amber-500" : inBox ? "bg-amber-500/15 border-amber-400/40" : "border-black/5 dark:border-white/5 hover:bg-emerald-500/20"}`}
                  >
                    {p ? (p.char?.emoji || EMOJI[p.type] || "▪") : ""}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
